import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Radio, CheckCircle, XCircle, X, MapPin, Calendar, Tag, Users, ExternalLink, Download, FileSpreadsheet } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { useIsAdmin } from "../../context/AuthContext";
import EventMiniMap from "../../components/EventMiniMap";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMPTY = { name: "", date: "", location: "", sector: "", future_presence: false, companies: [] };

function TradeshowDetailModal({ tradeshow, onClose }) {
  const { t } = useLanguage();
  if (!tradeshow) return null;
  const ex = Array.isArray(tradeshow.exhibitors) ? tradeshow.exhibitors : [];
  return (
    <div data-testid="tradeshow-modal" className="fixed inset-0 flex z-[9999]" onClick={onClose}>
      <div className="flex-1 bg-black/80 backdrop-blur-sm" />
      <div onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-[860px] bg-[#0A0C10] border-l-2 border-[#00E5FF] flex flex-col overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.6)]">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/[0.04] bg-[#0D0F12]">
          <div className="w-12 h-12 flex items-center justify-center border-2 border-[#00E5FF] bg-[#00E5FF]/10 flex-shrink-0">
            <Radio className="w-6 h-6 text-[#00E5FF]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
              {tradeshow.name}
            </h2>
            <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-[#CBD5E1]">
              <span className="text-[10px] px-2 py-0.5 border border-[#00E5FF] bg-[#00E5FF]/15 text-[#00E5FF] font-bold tracking-widest uppercase">
                {t("net.tradeshow.fair")}
              </span>
              {tradeshow.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{tradeshow.date}</span>}
              {tradeshow.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{tradeshow.location}</span>}
              {tradeshow.sector && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{tradeshow.sector}</span>}
            </div>
          </div>
          <button data-testid="tradeshow-modal-close" onClick={onClose}
            className="text-[#CBD5E1] hover:text-white hover:bg-[#1F242D] p-2 transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Action bar */}
        {ex.length > 0 && (
          <div className="flex items-center gap-2 px-6 py-3 border-b border-white/[0.04] bg-[#0A0C10]">
            <span className="text-[10px] text-[#A0AEC0] tracking-widest uppercase mr-2">Esporta lista:</span>
            <button data-testid="exhibitors-csv-btn"
              onClick={() => window.open(`${API}/network/tradeshows/${tradeshow.id}/exhibitors.csv`, "_blank")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66] text-xs font-bold tracking-wider uppercase hover:bg-[#00FF66]/20 transition-colors">
              <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
            </button>
            <button data-testid="exhibitors-pdf-btn"
              onClick={() => window.open(`${API}/network/tradeshows/${tradeshow.id}/exhibitors.pdf`, "_blank")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#A78BFA] bg-[#A78BFA]/10 text-[#A78BFA] text-xs font-bold tracking-wider uppercase hover:bg-[#A78BFA]/20 transition-colors">
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {tradeshow.description && (
            <section className="bg-[#0D0F12] border border-white/[0.04] p-5">
              <p className="text-[11px] text-[#A0AEC0] tracking-widest uppercase mb-2">{t("net.tradeshow.about")}</p>
              <p className="text-sm text-[#D1D5DB] leading-relaxed">{tradeshow.description}</p>
            </section>
          )}

          <section className="bg-[#0D0F12] border border-white/[0.04]">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.04]">
              <Users className="w-4 h-4 text-[#A78BFA]" />
              <p className="text-xs text-[#A0AEC0] tracking-widest uppercase">
                {ex.length} {t("net.tradeshow.exhibitors")}
              </p>
            </div>
            {ex.length === 0 ? (
              <div className="text-center py-8 text-xs text-[#A0AEC0]">{t("net.tradeshow.no_exhibitors")}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[#A0AEC0] tracking-widest uppercase border-b border-white/[0.04]">
                      {["Espositore", "Paese", "Settore", "Stand", "Sito"].map(h => (
                        <th key={h} className="text-left px-4 py-2.5 font-normal">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ex.map((e, i) => (
                      <tr key={`${e.name}-${i}`} data-testid="exhibitor-row"
                        className="border-b border-white/[0.04] last:border-0 hover:bg-[#1A1D24] transition-colors">
                        <td className="px-4 py-2.5 text-white font-medium">{e.name}</td>
                        <td className="px-4 py-2.5 text-[#CBD5E1]">{e.country || "—"}</td>
                        <td className="px-4 py-2.5 text-[#CBD5E1]">{e.sector || "—"}</td>
                        <td className="px-4 py-2.5 text-[#00E5FF] font-mono">{e.stand || "—"}</td>
                        <td className="px-4 py-2.5">
                          {e.website ? (
                            <a href={e.website} target="_blank" rel="noopener noreferrer"
                              data-testid="exhibitor-website"
                              className="inline-flex items-center gap-1 text-[#A78BFA] hover:underline">
                              {e.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : <span className="text-[#A0AEC0]">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default function Tradeshow() {
  const { t } = useLanguage();
  const isAdmin = useIsAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/network/tradeshows`, { withCredentials: true });
      setItems(data);
    } finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, companies: form.companies };
      await axios.post(`${API}/network/tradeshows`, payload, { withCredentials: true });
      setForm(EMPTY);
      setShowAdd(false);
      await load();
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    await axios.delete(`${API}/network/tradeshows/${id}`, { withCredentials: true });
    await load();
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{fontFamily: "Chivo, sans-serif"}}>{t("net.tradeshow.title")}</h1>
          <p className="text-xs text-[#A0AEC0] mt-1 tracking-widest uppercase">{t("net.tradeshow.subtitle")}</p>
        </div>
        <button data-testid="add-tradeshow-button" onClick={() => setShowAdd(!showAdd)} className={`flex items-center gap-2 bg-[#00E5FF] text-black font-semibold px-4 py-2 text-xs hover:bg-[#00B3CC] transition-colors ${isAdmin ? "" : "hidden"}`}>
          <Plus className="w-3.5 h-3.5" /> Add Tradeshow
        </button>
      </div>

      {/* Mini map */}
      <EventMiniMap
        events={items}
        accent="#00E5FF"
        Icon={Radio}
        title="Mappa B2B Fairs"
        maxItems={12}
        onEventClick={(ev) => setSelected(ev)}
      />

      {showAdd && isAdmin && (
        <form onSubmit={handleAdd} className="bg-[#0D0F12] border border-white/[0.04] p-5 space-y-4">
          <p className="text-xs text-[#A0AEC0] tracking-widest uppercase border-b border-white/[0.04] pb-3 mb-4">New Tradeshow</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[["name", "Nome Fiera *"], ["date", "Data (YYYY-MM-DD)"], ["location", "Luogo"], ["sector", "Settore"]].map(([f, l]) => (
              <div key={f}>
                <label className="block text-xs text-[#CBD5E1] tracking-wider uppercase mb-1.5">{l}</label>
                <input
                  value={form[f]}
                  onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))}
                  required={f === "name"}
                  className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="fp" checked={form.future_presence} onChange={e => setForm(p => ({ ...p, future_presence: e.target.checked }))} className="w-4 h-4 accent-[#00E5FF]" />
            <label htmlFor="fp" className="text-xs text-[#CBD5E1]">Presenza futura prevista</label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-xs bg-[#1F242D] text-white border border-white/[0.04] hover:bg-[#30363D]">Cancel</button>
            <button data-testid="save-tradeshow-button" type="submit" disabled={saving} className="px-4 py-2 text-xs bg-[#00E5FF] text-black font-semibold hover:bg-[#00B3CC] disabled:opacity-50">{saving ? "Saving..." : "Save"}</button>
          </div>
        </form>
      )}

      <div className="bg-[#0D0F12] border border-white/[0.04]">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.04]">
          <Radio className="w-4 h-4 text-[#00E5FF]" />
          <p className="text-xs text-[#A0AEC0] tracking-widest uppercase">{items.length} tradeshows · {t("net.tradeshow.click_hint")}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[#A0AEC0] tracking-widest uppercase border-b border-white/[0.04]">
                {["Nome", "Data", "Luogo", "Settore", "Espositori", "Presenza Futura", ...(isAdmin ? [""] : [])].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-[#A0AEC0]">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-[#A0AEC0]">No tradeshows yet.</td></tr>
              ) : items.map((item) => (
                <tr key={item.id} data-testid="tradeshow-row" onClick={() => setSelected(item)}
                  className="border-b border-white/[0.04] hover:bg-[#1A1D24] cursor-pointer transition-colors">
                  <td className="px-4 py-3 text-white font-medium hover:text-[#00E5FF]">{item.name}</td>
                  <td className="px-4 py-3 text-[#CBD5E1]">{item.date}</td>
                  <td className="px-4 py-3 text-[#CBD5E1]">{item.location}</td>
                  <td className="px-4 py-3 text-[#CBD5E1]">{item.sector}</td>
                  <td className="px-4 py-3 text-[#A78BFA] font-medium">{(item.exhibitors || []).length}</td>
                  <td className="px-4 py-3">{item.future_presence ? <CheckCircle className="w-4 h-4 text-[#00FF66]" /> : <XCircle className="w-4 h-4 text-[#A0AEC0]" />}</td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <button onClick={(e) => handleDelete(item.id, e)} className="text-[#A0AEC0] hover:text-[#FF3B30] transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TradeshowDetailModal tradeshow={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
